import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import OrderService from '../../services/order/order.service';

interface OrderData {
  orderID: string;
  createdDate: string;
  orderDetail: {
    expectedDeliveryDate: string;
  }[];
}

interface OrderReviewTopProps {
  orderData: OrderData;
}

const OrderReviewTop = ({ orderData }: OrderReviewTopProps) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date(orderData.orderDetail[0].expectedDeliveryDate));
  const [displayCalendar, setDisplayCalendar] = useState<boolean>(false);
  const [originalDate, setOriginalDate] = useState<Date | null>(new Date(orderData.orderDetail[0].expectedDeliveryDate));
  const toast = useRef<Toast>(null);
  const orderDate = orderData?.createdDate ? new Date(orderData.createdDate) : new Date();

  const handleDateChange = (e: any) => {
    setSelectedDate(e.value as Date | null);
    setDisplayCalendar(true);
  };

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSaveDate = async () => {
    if (selectedDate) { // Ensure selectedDate is not null
      try {
        // Format the date
        const formattedDate = formatDate(selectedDate);

        await OrderService.setOrderDeliveryDate(orderData.orderID, formattedDate);

        if (toast.current) {
          toast.current.show({ severity: 'success', summary: 'Success', detail: 'Date saved successfully' });
        }

        setOriginalDate(selectedDate); // Update original date to new date
        setDisplayCalendar(false); // Hide calendar and buttons
      } catch (error) {
        if (toast.current) {
          toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to save date' });
        }
      }
    } else {
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'No date selected' });
      }
    }
  };


  const handleCancel = () => {
    setSelectedDate(originalDate); // Revert to original date
    setDisplayCalendar(false); // Hide calendar and buttons
  };

  return (
    <>
      <div className="grid order-top-color mb-3 px-3">
        <div className="col-12 md:col-6">
          <b>Order #: {orderData.orderID}</b>
        </div>
        <div className="col-12 md:col-6 text-right left-mobile">
          <p className="mb-1 font-semibold">
            {t("ordersummary_order_date")} :{" "}
            {new Date(orderData.createdDate).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <p className="mb-1 font-semibold">
              Estimated Delivery Date (On or before):
            </p>
            <Calendar
              value={selectedDate}
              onChange={handleDateChange}
              showButtonBar={false} // Hide default button bar
              dateFormat="dd/mm/yy"
              style={{ width: 'auto', minWidth: '12rem' }}
              minDate={orderDate}
            />
            {displayCalendar && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-start' }}>
                <Button icon="pi pi-check" onClick={handleSaveDate} />
                <Button icon="pi pi-times" onClick={handleCancel} />
              </div>
            )}

          </div>
          <Toast ref={toast} />
        </div>
      </div>
    </>
  );
};

export default OrderReviewTop;
